import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import MicIcon from "@material-ui/icons/Mic";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AudioRecorder from "../AudioRecorder";
import MessageVariablesPicker from "../MessageVariablesPicker";

const detectMediaType = (fileName = "", mimeType = "") => {
  const mime = String(mimeType).toLowerCase();
  const ext = String(fileName).toLowerCase().split(".").pop();
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) return "image";
  if (mime.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "aac", "opus"].includes(ext)) return "audio";
  if (mime.startsWith("video/") || ["mp4", "avi", "mov", "mkv", "webm", "3gp"].includes(ext)) return "video";
  return "document";
};

const basename = value => String(value || "").split("?")[0].split("#")[0].split("/").pop();
const newTextItem = () => ({
  key: `text-${Date.now()}-${Math.random()}`,
  kind: "text",
  text: "",
  delaySeconds: 0
});

const componentToItem = component => {
  if (component.type === "sequence_text") {
    let meta = {};
    try { meta = JSON.parse(component.example || "{}"); } catch (_) {}
    return {
      key: `existing-${component.id}`,
      kind: "text",
      text: component.text || "",
      delaySeconds: Number(meta.delaySeconds || 0)
    };
  }
  if (component.type !== "sequence_media") return null;
  try {
    const meta = JSON.parse(component.example || "{}");
    return {
      key: `existing-${component.id}`,
      kind: "media",
      sourceId: component.id,
      storedName: meta.storedName,
      originalName: meta.originalName || component.text || meta.storedName,
      mimeType: meta.mimeType || "application/octet-stream",
      mediaType: meta.mediaType || component.format || "document",
      delaySeconds: Number(meta.delaySeconds || 0)
    };
  } catch (_) {
    return null;
  }
};

const QuickMessageSequenceDialog = ({ open, onClose, quickemessageId, reload }) => {
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [shortcode, setShortcode] = useState("");
  const [category, setCategory] = useState("");
  const [visao, setVisao] = useState(true);
  const [geral, setGeral] = useState(true);
  const [items, setItems] = useState([newTextItem()]);
  const [activeTextIndex, setActiveTextIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setShortcode("");
    setCategory("");
    setVisao(true);
    setGeral(true);
    setItems([newTextItem()]);
    setActiveTextIndex(0);
    setRecording(false);
  };

  useEffect(() => {
    if (!open) return;
    if (!quickemessageId) {
      reset();
      return;
    }
    let mounted = true;
    setLoading(true);
    api.get(`/quick-messages/${quickemessageId}`)
      .then(({ data }) => {
        if (!mounted) return;
        setShortcode(data.shortcode || "");
        setCategory(data.category || "");
        setVisao(data.visao === true);
        setGeral(data.geral !== false);
        const sequence = (data.components || [])
          .filter(component => component.type === "sequence_text" || component.type === "sequence_media")
          .sort((a, b) => Number(a.buttons || a.id) - Number(b.buttons || b.id))
          .map(componentToItem)
          .filter(Boolean);
        if (sequence.length > 0) {
          setItems(sequence);
          return;
        }
        const legacy = [];
        if (data.message) legacy.push({ ...newTextItem(), text: data.message });
        if (data.mediaPath) {
          legacy.push({
            key: `legacy-${data.id}`,
            kind: "media",
            legacyMedia: true,
            storedName: basename(data.mediaPath),
            originalName: data.mediaName || basename(data.mediaPath),
            mimeType: "application/octet-stream",
            mediaType: data.mediaType || detectMediaType(data.mediaName),
            delaySeconds: 0
          });
        }
        setItems(legacy.length ? legacy : [newTextItem()]);
      })
      .catch(toastError)
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [open, quickemessageId]);

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const updateText = (index, text) => {
    setItems(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, text } : item));
  };

  const addFiles = files => {
    const additions = Array.from(files || []).map(file => ({
      key: `file-${Date.now()}-${Math.random()}`,
      kind: "media",
      file,
      originalName: file.name,
      mimeType: file.type || "application/octet-stream",
      mediaType: detectMediaType(file.name, file.type),
      delaySeconds: 0
    }));
    if (additions.length) setItems(current => [...current, ...additions].slice(0, 20));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addRecordedAudio = blob => {
    const originalName = `audio_${Date.now()}.webm`;
    setItems(current => [...current, {
      key: `audio-${Date.now()}`,
      kind: "media",
      file: blob,
      originalName,
      mimeType: blob.type || "audio/webm",
      mediaType: "audio",
      delaySeconds: 0
    }].slice(0, 20));
    setRecording(false);
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    setItems(current => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setActiveTextIndex(target);
  };

  const remove = index => {
    setItems(current => current.filter((_, itemIndex) => itemIndex !== index));
    setActiveTextIndex(0);
  };

  const updateDelayPart = (index, part, rawValue) => {
    const numeric = Math.max(0, Math.min(59, Number.parseInt(rawValue || "0", 10) || 0));
    setItems(current => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const currentDelay = Number(item.delaySeconds || 0);
      const minutes = Math.floor(currentDelay / 60);
      const seconds = currentDelay % 60;
      return {
        ...item,
        delaySeconds: part === "minutes"
          ? numeric * 60 + seconds
          : minutes * 60 + numeric
      };
    }));
  };

  const appendVariable = value => {
    const index = items[activeTextIndex]?.kind === "text"
      ? activeTextIndex
      : items.findIndex(item => item.kind === "text");
    if (index < 0) return;
    updateText(index, `${items[index].text || ""}${value}`);
  };

  const save = async () => {
    const cleanShortcode = shortcode.trim();
    if (!cleanShortcode) return toast.error("Informe o atalho da resposta rápida.");
    if (!items.length) return toast.error("Adicione pelo menos um item à sequência.");
    if (items.some(item => item.kind === "text" && !String(item.text || "").trim())) {
      return toast.error("Preencha ou remova os blocos de texto vazios.");
    }

    setLoading(true);
    const formData = new FormData();
    const files = [];
    const payloadItems = items.map(item => {
      if (item.kind === "text") {
        return {
          kind: "text",
          text: item.text.trim(),
          delaySeconds: Number(item.delaySeconds || 0)
        };
      }
      if (item.file) {
        const fileIndex = files.length;
        files.push({ file: item.file, name: item.originalName || `arquivo_${fileIndex}` });
        return { kind: "media", fileIndex, delaySeconds: Number(item.delaySeconds || 0) };
      }
      return {
        kind: "media",
        sourceId: item.sourceId,
        legacyMedia: item.legacyMedia === true,
        storedName: item.storedName,
        originalName: item.originalName,
        mimeType: item.mimeType,
        mediaType: item.mediaType,
        delaySeconds: Number(item.delaySeconds || 0)
      };
    });
    formData.append("typeArch", "quickMessage");
    formData.append("payload", JSON.stringify({
      shortcode: cleanShortcode,
      category: category.trim() || null,
      visao,
      geral,
      items: payloadItems
    }));
    files.forEach(entry => formData.append("files", entry.file, entry.name));

    try {
      const url = quickemessageId
        ? `/quick-messages/${quickemessageId}/sequence`
        : "/quick-messages/sequence";
      const method = quickemessageId ? "put" : "post";
      await api[method](url, formData);
      toast.success("Resposta rápida salva com a sequência definida.");
      if (typeof reload === "function") reload();
      reset();
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{quickemessageId ? "Editar resposta rápida" : "Nova resposta rápida em sequência"}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
          <TextField
            label="Atalho"
            value={shortcode}
            onChange={event => setShortcode(event.target.value)}
            variant="outlined"
            size="small"
            disabled={loading}
            fullWidth
          />
          <TextField
            label="Categoria (opcional)"
            value={category}
            onChange={event => setCategory(event.target.value)}
            variant="outlined"
            size="small"
            disabled={loading}
            fullWidth
          />
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 210 }}>
              <InputLabel>Visibilidade</InputLabel>
              <Select value={visao ? "public" : "personal"} onChange={event => setVisao(event.target.value === "public")} label="Visibilidade">
                <MenuItem value="public">Pública — toda a equipe</MenuItem>
                <MenuItem value="personal">Pessoal — apenas eu</MenuItem>
              </Select>
            </FormControl>
            {visao && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
                <InputLabel>Status</InputLabel>
                <Select value={geral ? "active" : "inactive"} onChange={event => setGeral(event.target.value === "active")} label="Status">
                  <MenuItem value="active">Ativa</MenuItem>
                  <MenuItem value="inactive">Inativa</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 10, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 800 }}>Sequência de envio</Typography>
              <Typography variant="caption" color="textSecondary">Os itens serão enviados de cima para baixo, exatamente nesta ordem.</Typography>
            </Box>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <Button startIcon={<TextFieldsIcon />} variant="outlined" onClick={() => setItems(current => [...current, newTextItem()].slice(0, 20))} disabled={loading || items.length >= 20}>Adicionar texto</Button>
              <Button startIcon={<AttachFileIcon />} variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={loading || items.length >= 20}>Adicionar arquivo</Button>
              <Button startIcon={<MicIcon />} variant="outlined" onClick={() => setRecording(true)} disabled={loading || recording || items.length >= 20}>Gravar áudio</Button>
              <input ref={fileInputRef} type="file" multiple accept="*/*" hidden onChange={event => addFiles(event.target.files)} />
            </Box>
          </Box>

          {recording && (
            <Box style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
              <AudioRecorder onAudioRecorded={addRecordedAudio} onAudioDeleted={() => setRecording(false)} disabled={loading} />
            </Box>
          )}

          {items.map((item, index) => (
            <Box key={item.key} style={{ border: "1px solid #d8dde3", borderRadius: 10, padding: 12, background: "rgba(0,0,0,0.015)" }}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Chip label={index + 1} color="primary" size="small" />
                <Box flex={1}>
                  {item.kind === "text" ? (
                    <TextField
                      label="Texto"
                      value={item.text}
                      onFocus={() => setActiveTextIndex(index)}
                      onChange={event => updateText(index, event.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                      disabled={loading}
                      fullWidth
                    />
                  ) : (
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>{item.originalName || "Arquivo"}</Typography>
                      <Chip size="small" label={item.mediaType || detectMediaType(item.originalName, item.mimeType)} variant="outlined" />
                    </Box>
                  )}
                </Box>
                <IconButton size="small" onClick={() => move(index, -1)} disabled={loading || index === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => move(index, 1)} disabled={loading || index === items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="secondary" onClick={() => remove(index)} disabled={loading}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
              {index < items.length - 1 && (
                <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 10, marginLeft: 40 }}>
                  <Typography variant="caption" color="textSecondary">Intervalo até o próximo bloco</Typography>
                  <TextField
                    value={String(Math.floor(Number(item.delaySeconds || 0) / 60)).padStart(2, "0")}
                    onChange={event => updateDelayPart(index, "minutes", event.target.value)}
                    inputProps={{ min: 0, max: 59, style: { textAlign: "center", width: 34 } }}
                    type="number"
                    size="small"
                    variant="outlined"
                    disabled={loading}
                  />
                  <Typography style={{ fontWeight: 800 }}>:</Typography>
                  <TextField
                    value={String(Number(item.delaySeconds || 0) % 60).padStart(2, "0")}
                    onChange={event => updateDelayPart(index, "seconds", event.target.value)}
                    inputProps={{ min: 0, max: 59, style: { textAlign: "center", width: 34 } }}
                    type="number"
                    size="small"
                    variant="outlined"
                    disabled={loading}
                  />
                  <Typography variant="caption" color="textSecondary">min : seg — mostra “digitando...” durante a espera</Typography>
                </Box>
              )}
            </Box>
          ))}

          {items.some(item => item.kind === "text") && (
            <MessageVariablesPicker disabled={loading} showSchedulingVars onClick={appendVariable} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={save} disabled={loading}>
          {loading ? "Salvando..." : "Salvar resposta rápida"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickMessageSequenceDialog;
